import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { AlertCircle, ArrowDown, ArrowUp, CheckCircle2, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { BillingConfirmationDialog } from '@/components/billing/BillingConfirmationDialog';
import { SecurePaymentDialog } from '@/components/billing/SecurePaymentDialog';
import type { MoyasarPaymentFormConfig } from '@/components/billing/MoyasarPaymentForm';
import {
  UpgradePricingSummary,
  type AuthoritativeUpgradePricing,
} from '@/components/billing/UpgradePricingSummary';
import type { BillingReadiness } from '@/hooks/useBillingReadiness';
import {
  activateMockPaidCheckout,
  canActivateMockPaidCheckout,
  getCheckoutRedirectUrl,
} from '@/lib/billing/paymentCheckout';
import {
  bindPaymentIntentKeyToTransaction,
  paymentAppError,
  paymentIntentKey,
  settlePaymentIntentKey,
  type PaymentIntentKeyInput,
} from '@/lib/paymentSafety';
import { showAppError } from '@/lib/errors';
import { buildBillingConsentBinding, CONSENT_TEXT_VERSION } from '@/lib/billing/consentPaymentBinding';
import {
  isPayablePricingDisplayContract,
  type PricingDisplayContract,
} from '@/lib/billing/pricingDisplay';
import { DowngradePricingSummary } from '@/components/billing/DowngradePricingSummary';

type BillingOwner = 'organization' | 'candidate';
type Portal = 'forkhire' | 'forkseek';
type PlanChangeJourneyStep = 'quote' | 'billing_confirmation' | 'payment' | 'completed';

interface PlanTarget {
  id: string;
  plan_name: string;
}

interface CommercialPlanQuote {
  classification:
    | 'same_active_plan'
    | 'upgrade'
    | 'downgrade'
    | 'already_paid_target_value'
    | 'initial_paid_subscription'
    | 'scheduled_renewal'
    | 'blocked';
  blocker_reason?: string;
  current_plan_name?: string;
  target_plan_name?: string;
  current_paid_cycle_value_sar?: number;
  upgrade_credit_value_sar?: number;
  target_plan_value_sar?: number;
  target_price_sar?: number;
  net_upgrade_charge_sar?: number;
  upgrade_charge_sar?: number;
  upgrade_difference_before_vat_sar?: number;
  vat_rate_percent?: number;
  vat_amount_sar?: number;
  total_due_today_sar?: number;
  total_due_today_halalas?: number;
  next_renewal_price_sar?: number;
  next_renewal_date?: string | null;
  renewal_date?: string | null;
  billing_cycle?: 'monthly' | 'yearly';
  error?: string;
  pricing_display?: PricingDisplayContract;
  target_plan_pricing_display?: PricingDisplayContract;
  paid_cycle_credit_pricing_display?: PricingDisplayContract;
  headline_rounding_adjustment_sar?: number;
  renewal_pricing_display?: PricingDisplayContract;
  tax_config_id?: string;
}

interface ActiveSubscriptionPlanChangeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ownerType: BillingOwner;
  ownerId?: string;
  portal: Portal;
  targetPlan: PlanTarget | null;
  client: SupabaseClient;
  callbackUrl: string;
  editBillingPath: string;
  billingReadiness?: BillingReadiness;
  isBillingReadinessLoading?: boolean;
}

function authoritativeUpgradePricing(
  quote: CommercialPlanQuote | undefined,
): AuthoritativeUpgradePricing | null {
  if (quote?.classification !== 'upgrade') return null;
  const values = {
    differenceBeforeVatSar: Number(quote.upgrade_difference_before_vat_sar),
    vatRatePercent: Number(quote.vat_rate_percent),
    vatAmountSar: Number(quote.vat_amount_sar),
    totalDueTodaySar: Number(quote.total_due_today_sar),
    totalDueTodayHalalas: Number(quote.total_due_today_halalas),
    nextRenewalPriceSar: Number(quote.next_renewal_price_sar),
    nextRenewalDate: String(quote.next_renewal_date ?? ''),
    pricingDisplay: quote.pricing_display,
    targetPlanPricingDisplay: quote.target_plan_pricing_display,
    paidCycleCreditPricingDisplay: quote.paid_cycle_credit_pricing_display,
    headlineRoundingAdjustmentSar: Number(
      quote.headline_rounding_adjustment_sar,
    ),
    renewalPricingDisplay: quote.renewal_pricing_display,
  };
  const numericValues = [
    values.differenceBeforeVatSar,
    values.vatRatePercent,
    values.vatAmountSar,
    values.totalDueTodaySar,
    values.totalDueTodayHalalas,
    values.nextRenewalPriceSar,
    values.headlineRoundingAdjustmentSar,
  ];
  if (
    numericValues.some((value) => !Number.isFinite(value)) ||
    values.differenceBeforeVatSar <= 0 ||
    values.vatRatePercent < 0 ||
    values.vatAmountSar < 0 ||
    values.totalDueTodaySar <= 0 ||
    !Number.isInteger(values.totalDueTodayHalalas) ||
    values.totalDueTodayHalalas <= 0 ||
    values.nextRenewalPriceSar <= 0 ||
    !values.nextRenewalDate
    || !values.pricingDisplay
    || !isPayablePricingDisplayContract(values.targetPlanPricingDisplay)
    || !isPayablePricingDisplayContract(values.paidCycleCreditPricingDisplay)
    || !values.renewalPricingDisplay
  ) {
    return null;
  }
  return values;
}

export function ActiveSubscriptionPlanChangeDialog({
  open,
  onOpenChange,
  ownerType,
  ownerId,
  portal,
  targetPlan,
  client,
  callbackUrl,
  editBillingPath,
  billingReadiness,
  isBillingReadinessLoading = false,
}: ActiveSubscriptionPlanChangeDialogProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [journeyStep, setJourneyStep] = useState<PlanChangeJourneyStep>('quote');
  const [paymentForm, setPaymentForm] = useState<MoyasarPaymentFormConfig | null>(null);
  const submissionLockedRef = useRef(false);

  const quoteQuery = useQuery<CommercialPlanQuote>({
    queryKey: ['commercial-plan-quote', ownerType, ownerId, targetPlan?.id],
    enabled: open && !!ownerId && !!targetPlan?.id,
    queryFn: async () => {
      const { data, error } = await client.functions.invoke('quote-upgrade-price', {
        body: {
          owner_type: ownerType,
          owner_id: ownerId,
          target_plan_id: targetPlan!.id,
        },
      });
      if (error) throw error;
      return data as CommercialPlanQuote;
    },
  });

  useEffect(() => {
    if (!open) return;
    setJourneyStep('quote');
    setPaymentForm(null);
    submissionLockedRef.current = false;
  }, [open, targetPlan?.id]);

  useEffect(() => {
    if (open) return;
    setJourneyStep('quote');
    setPaymentForm(null);
    submissionLockedRef.current = false;
    queryClient.removeQueries({
      queryKey: ['commercial-plan-quote', ownerType, ownerId, targetPlan?.id],
      exact: true,
    });
  }, [open, ownerId, ownerType, queryClient, targetPlan?.id]);

  const quote = quoteQuery.data;
  const classification = quote?.classification;
  const isUpgrade = classification === 'upgrade';
  const isDowngrade = classification === 'downgrade';
  const isAlreadyPaid = classification === 'already_paid_target_value';
  const isSamePlan = classification === 'same_active_plan';
  const upgradePricing = authoritativeUpgradePricing(quote);
  const renewalDate = quote?.next_renewal_date ?? quote?.renewal_date;
  const language = i18n.resolvedLanguage ?? i18n.language;
  const finalPlanPricingLoading = quoteQuery.isLoading || quoteQuery.isFetching;
  const finalPlanPricingReady = !finalPlanPricingLoading &&
    isPayablePricingDisplayContract(quote?.renewal_pricing_display) &&
    (isDowngrade || isPayablePricingDisplayContract(quote?.pricing_display));

  const attempt = useMemo<PaymentIntentKeyInput | null>(() => {
    if (!ownerId || !targetPlan) return null;
    return {
      ownerType,
      ownerId,
      paymentType: isDowngrade ? 'downgrade' : 'upgrade',
      productKey: targetPlan.id,
      billingCycle: quote?.billing_cycle,
    };
  }, [isDowngrade, ownerId, ownerType, quote?.billing_cycle, targetPlan]);

  const planChangeMutation = useMutation({
    mutationFn: async () => {
      if (!ownerId || !targetPlan || !attempt || (!isUpgrade && !isDowngrade)) {
        throw new Error(t('commercialPlanChange.errors.quoteRequired'));
      }
      const consentBinding = isUpgrade
        ? buildBillingConsentBinding({
            planId: targetPlan.id,
            billingCycle: quote?.billing_cycle === 'yearly' ? 'yearly' : 'monthly',
            taxConfigId: quote?.tax_config_id,
            duePricing: quote?.pricing_display,
            introPricing: null,
            renewalPricing: quote?.renewal_pricing_display,
            consentLanguage: language,
          })
        : null;
      if (isUpgrade && !consentBinding) {
        throw new Error(t('commercialPlanChange.errors.pricingMismatch'));
      }

      const { data, error } = await client.functions.invoke('create-payment', {
        body: {
          owner_type: ownerType,
          owner_id: ownerId,
          payment_type: isDowngrade ? 'subscription' : 'upgrade',
          idempotency_key: paymentIntentKey(attempt),
          target_plan_id: targetPlan.id,
          description: isDowngrade ? 'Governed plan downgrade' : 'Governed plan upgrade',
          callback_url: callbackUrl,
          ...(consentBinding ? { billing_consent: consentBinding } : {}),
          metadata: {
            billing_consent_version: CONSENT_TEXT_VERSION,
            billing_consent_accepted: 'true',
            billing_confirmation_confirmed: 'true',
          },
        },
      });

      if (error) {
        let payload: Record<string, unknown> | null = null;
        const response = (error as { context?: Response }).context;
        if (response instanceof Response) {
          try {
            payload = await response.clone().json() as Record<string, unknown>;
          } catch {
            // Keep the SDK message if the safe response body is unavailable.
          }
        }
        if (payload?.reason === 'upgrade_net_difference_not_positive') {
          throw new Error(t('commercialPlanChange.alreadyPaid.message', { plan: targetPlan.plan_name }));
        }
        throw paymentAppError(
          payload ?? error,
          language,
          error.message,
          t('commercialPlanChange.errors.paymentFailed'),
        );
      }
      if (data?.error) {
        if (data.reason === 'upgrade_net_difference_not_positive') {
          throw new Error(t('commercialPlanChange.alreadyPaid.message', { plan: targetPlan.plan_name }));
        }
        throw paymentAppError(
          data,
          language,
          t('commercialPlanChange.errors.paymentFailed'),
          t('commercialPlanChange.errors.paymentFailed'),
        );
      }
      return data;
    },
    onSuccess: async (data) => {
      if (data.status === 'downgrade_scheduled') {
        setJourneyStep('completed');
        onOpenChange(false);
        toast.success(t('commercialPlanChange.downgrade.scheduledTitle'), {
          description: t('commercialPlanChange.downgrade.scheduledMessage'),
        });
        await queryClient.invalidateQueries({
          queryKey: [ownerType === 'organization' ? 'org-subscription' : 'seek-subscription'],
        });
        return;
      }

      bindPaymentIntentKeyToTransaction(attempt!, data.transaction_id);
      settlePaymentIntentKey(attempt!, data.status);
      await queryClient.invalidateQueries({
        queryKey: [ownerType === 'organization'
          ? 'org-pending-plan-change'
          : 'seek-pending-plan-change'],
      });

      if (data.payment_form) {
        const displayedTotalHalalas = upgradePricing?.totalDueTodayHalalas;
        const paymentFormAmount = Number(data.payment_form.amount);
        if (
          !Number.isInteger(displayedTotalHalalas) ||
          !Number.isInteger(paymentFormAmount) ||
          displayedTotalHalalas !== paymentFormAmount
        ) {
          showAppError(
            new Error(t('commercialPlanChange.errors.pricingMismatch')),
            {
              product: portal === 'forkseek' ? 'forkseek' : 'forkhire',
              domain: 'billing',
            },
          );
          return;
        }
        setPaymentForm(data.payment_form);
        setJourneyStep('payment');
        return;
      }

      const redirectUrl = getCheckoutRedirectUrl(data);
      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }

      if (!canActivateMockPaidCheckout(data)) {
        throw new Error(t('commercialPlanChange.errors.checkoutUnavailable'));
      }

      await activateMockPaidCheckout(client, data);
      settlePaymentIntentKey(attempt!, 'paid_and_activated');
      setJourneyStep('completed');
      onOpenChange(false);
      toast.success(t('commercialPlanChange.upgrade.activated'));
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [ownerType === 'organization' ? 'org-subscription' : 'seek-subscription'],
        }),
        queryClient.invalidateQueries({ queryKey: ['entitlement'] }),
        queryClient.invalidateQueries({ queryKey: ['plan-usage'] }),
        queryClient.invalidateQueries({
          queryKey: [ownerType === 'organization'
            ? 'org-billing-history-transactions-recent'
            : 'seek-transactions'],
        }),
        queryClient.invalidateQueries({
          queryKey: [ownerType === 'organization'
            ? 'org-billing-history-invoices-recent'
            : 'seek-subscription-invoices'],
        }),
        queryClient.invalidateQueries({
          queryKey: [ownerType === 'organization'
            ? 'org-pending-plan-change'
            : 'seek-pending-plan-change'],
        }),
      ]);
    },
    onError: (error: unknown) => {
      showAppError(error, {
        product: portal === 'forkseek' ? 'forkseek' : 'forkhire',
        domain: 'billing',
      });
    },
  });

  const finishJourney = () => {
    setJourneyStep('completed');
    setPaymentForm(null);
    onOpenChange(false);
  };

  const cancelJourney = () => {
    if (!planChangeMutation.isPending) finishJourney();
  };

  const submitPlanChange = () => {
    if (submissionLockedRef.current || planChangeMutation.isPending) return;
    submissionLockedRef.current = true;
    planChangeMutation.mutate(undefined, {
      onSettled: () => {
        submissionLockedRef.current = false;
      },
    });
  };

  const beginAction = () => {
    if (isUpgrade && upgradePricing) {
      setJourneyStep('billing_confirmation');
      return;
    }
    if (isDowngrade) submitPlanChange();
  };

  const handleBillingOpenChange = (next: boolean) => {
    if (next) {
      setJourneyStep('billing_confirmation');
      return;
    }
    if (!planChangeMutation.isPending) setJourneyStep('quote');
  };

  const returnFromPayment = () => {
    setPaymentForm(null);
    setJourneyStep('billing_confirmation');
  };

  return (
    <>
      {journeyStep === 'quote' && (
        <Dialog
          open={open}
          onOpenChange={(next) => { if (!next) cancelJourney(); }}
        >
          <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isDowngrade ? <ArrowDown className="h-5 w-5" /> : <ArrowUp className="h-5 w-5" />}
              {t('commercialPlanChange.title', { plan: targetPlan?.plan_name ?? '' })}
            </DialogTitle>
            <DialogDescription>{t('commercialPlanChange.description')}</DialogDescription>
          </DialogHeader>

          {quoteQuery.isLoading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              {t('commercialPlanChange.loading')}
            </div>
          ) : quoteQuery.isError || quote?.error ? (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              <AlertCircle className="me-2 inline h-4 w-4" />
              {t('commercialPlanChange.errors.quoteFailed')}
            </div>
          ) : isAlreadyPaid ? (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                <div>
                  <p className="font-semibold text-foreground">{t('commercialPlanChange.alreadyPaid.title')}</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('commercialPlanChange.alreadyPaid.message', { plan: targetPlan?.plan_name ?? '' })}
                  </p>
                </div>
              </div>
            </div>
          ) : isSamePlan ? (
            <div className="rounded-lg border border-border p-4 text-sm text-muted-foreground">
              {t('commercialPlanChange.samePlan')}
            </div>
          ) : isUpgrade && upgradePricing ? (
            <UpgradePricingSummary pricing={upgradePricing} />
          ) : isUpgrade ? (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              <AlertCircle className="me-2 inline h-4 w-4" />
              {t('commercialPlanChange.errors.quoteFailed')}
            </div>
          ) : isDowngrade ? (
            <DowngradePricingSummary
              targetPlan={targetPlan?.plan_name ?? ''}
              effectiveDate={renewalDate}
              renewalPricing={quote?.renewal_pricing_display}
            />
          ) : (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              {t('commercialPlanChange.errors.unsupportedResult')}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" disabled={planChangeMutation.isPending} onClick={cancelJourney}>
              {t('common.close')}
            </Button>
            {(isUpgrade || isDowngrade) && (
              <Button
                type="button"
                disabled={
                  quoteQuery.isLoading ||
                  planChangeMutation.isPending ||
                  (isUpgrade && !upgradePricing)
                }
                onClick={beginAction}
              >
                {planChangeMutation.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                {isDowngrade
                  ? t('commercialPlanChange.downgrade.confirm')
                  : t('commercialPlanChange.upgrade.continue')}
              </Button>
            )}
          </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {journeyStep === 'billing_confirmation' && (
        <BillingConfirmationDialog
          open={open}
          onOpenChange={handleBillingOpenChange}
          ownerType={ownerType}
          ownerId={ownerId}
          client={client}
          editPath={editBillingPath}
          brand={portal}
          readiness={billingReadiness}
          isReadinessLoading={isBillingReadinessLoading}
          isProcessing={planChangeMutation.isPending}
          pricingSummary={upgradePricing
            ? <UpgradePricingSummary pricing={upgradePricing} />
            : undefined}
          pricingReady={finalPlanPricingReady}
          pricingLoading={finalPlanPricingLoading}
          onConfirm={submitPlanChange}
        />
      )}

      {journeyStep === 'payment' && paymentForm && (
        <SecurePaymentDialog
          open={open}
          config={paymentForm}
          client={client}
          brand={portal}
          title={t('commercialPlanChange.paymentDetails')}
          pricingSummary={upgradePricing
            ? <UpgradePricingSummary pricing={upgradePricing} />
            : undefined}
          onBack={returnFromPayment}
        />
      )}
    </>
  );
}
